// ─────────────────────────────────────────────────────────────────────────────
// V2Import — /dashboard/importproducts in the v2 design.
// Same endpoints and multipart fields as v1: POST /api/migration/{products|clients|invoices}/import
// with `file` + `selectedColumns` (JSON array). CSV preview via PapaParse, as v1.
// New: guided steps, downloadable template per type, required-column check,
// drag & drop, inline errors (no alert()).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useRef, useState } from "react";
import Papa from "papaparse";
import { useNavigate } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { PageHead } from "./kit";

const TYPES = [
  {
    key: "products", label: "Products", glyph: "box", blurb: "Your catalogue with prices, GST, stock and reorder levels",
    endpoint: "/api/migration/products/import", required: ["name", "sku", "sellingPrice"],
    columns: ["name", "sku", "hsnCode", "unit", "sellingPrice", "purchasePrice", "gstRate", "currentStock", "reorderLevel"],
    example: ["LED Bulb 9W", "LED-001", "85395000", "pcs", "150", "120", "18", "500", "50"],
    hints: ["Unit: pcs, kg, g, l, ml, m, box, set (default pcs)", "GST rate: 0, 5, 12, 18 or 28 (default 18)", "SKU must be unique", "Reorder level defaults to 0"],
    after: "/dashboard/managestock",
  },
  {
    key: "clients", label: "Clients", glyph: "clients", blurb: "Customers with GSTIN, address and contact details",
    endpoint: "/api/migration/clients/import", required: ["clientName", "clientGSTIN", "clientAddress"],
    columns: ["clientName", "clientGSTIN", "clientAddress", "clientEmail", "clientPhone"],
    example: ["ABC Corporation", "29ABCDE1234F1Z5", "123 MG Road, Bangalore", "contact@abc.com", "+91-9876543210"],
    hints: ["GSTIN must be 15 characters", "Wrap addresses containing commas in quotes"],
    after: "/dashboard/manageclients",
  },
  {
    key: "invoices", label: "Invoices", glyph: "invoice", blurb: "Past invoices from another system — one row per line item",
    endpoint: "/api/migration/invoices/import", required: ["invoiceNumber", "invoiceDate", "clientName"],
    columns: ["invoiceNumber", "invoiceDate", "clientName", "clientGSTIN", "clientAddress", "itemName", "itemSKU", "itemHSN", "itemQty", "itemPrice", "itemGSTRate", "subtotal", "totalCGST", "totalSGST", "totalIGST", "grandTotal"],
    example: null,
    hints: ["Rows sharing an invoiceNumber become line items of one invoice", "Clients are created if the GSTIN isn't found", "Products are created when itemSKU is given"],
    after: "/dashboard/viewinvoices",
  },
];

const csvCell = (v) => (/[",\n]/.test(v) ? `"${String(v).replace(/"/g, '""')}"` : v);

export default function V2Import() {
  usePageTitle("Import");
  const navigate = useNavigate();
  const [typeKey, setTypeKey] = useState("products");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [cols, setCols] = useState({});
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const input = useRef(null);
  const T = TYPES.find((t) => t.key === typeKey);
  const step = result ? 4 : preview ? 3 : 2;

  const reset = () => { setFile(null); setPreview(null); setCols({}); setErr(""); setResult(null); };
  const choose = (k) => { setTypeKey(k); reset(); };

  const template = () => {
    const lines = [T.columns.join(",")];
    if (T.example) lines.push(T.example.map(csvCell).join(","));
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    a.download = `eazybilling-${T.key}-template.csv`; a.click();
  };

  const take = (f) => {
    if (!f) return;
    setErr(""); setResult(null);
    if (!/\.csv$/i.test(f.name)) { setErr("Please choose a .csv file."); return; }
    setFile(f);
    Papa.parse(f, {
      header: true, skipEmptyLines: true,
      complete: (res) => {
        const columns = res.meta.fields || [];
        setPreview({ columns, rows: res.data.slice(0, 8), total: res.data.length });
        setCols(Object.fromEntries(columns.map((c) => [c, true])));
      },
      error: (e) => setErr(`Couldn't read the CSV: ${e.message}`),
    });
  };

  const selected = Object.keys(cols).filter((c) => cols[c]);
  const missing = preview ? T.required.filter((r) => !selected.includes(r)) : [];

  const run = async () => {
    if (!file || !selected.length) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("selectedColumns", JSON.stringify(selected));
      const r = await fetch(`${API_BASE}${T.endpoint}`, { method: "POST", headers: { "auth-token": localStorage.getItem("token") }, body: fd });
      let d; try { d = await r.json(); } catch { throw new Error("The server didn't return a result."); }
      if (!r.ok) throw new Error(d.message || "Import failed");
      setResult({ imported: d.imported || 0, skipped: d.skipped || 0, totalInvoices: d.totalInvoices, errors: d.errors || d.skippedDetails || [] });
      setPreview(null);
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };

  return (
    <div className="v2p v2im">
      <PageHead eyebrow="Inventory" title="Import" sub="Bring products, clients or past invoices in from a spreadsheet." />

      <ol className="v2im-steps" aria-label="Progress">
        {["Choose data", "Upload CSV", "Check columns", "Done"].map((l, i) => (
          <li key={l} className={i + 1 < step ? "is-done" : i + 1 === step ? "is-now" : ""}>
            <span>{i + 1 < step ? <Glyph name="check" size={12} stroke={3} /> : i + 1}</span>{l}
          </li>
        ))}
      </ol>

      <div className="v2im-types" role="radiogroup" aria-label="What to import">
        {TYPES.map((t) => (
          <button key={t.key} type="button" role="radio" aria-checked={typeKey === t.key} className={`v2im-type${typeKey === t.key ? " is-on" : ""}`} onClick={() => choose(t.key)}>
            <Duo name={t.glyph} size={34} />
            <span><strong>{t.label}</strong><span>{t.blurb}</span></span>
          </button>
        ))}
      </div>

      <div className="v2im-grid">
        <section className="v2-card v2im-main">
          {result ? (
            <div className="v2im-result">
              <span className={`v2ci-done-check${result.imported ? "" : " is-warn"}`}><Glyph name={result.imported ? "check" : "alert"} size={30} stroke={2.6} /></span>
              <h2>{result.imported ? `${result.imported} ${T.key === "invoices" ? "rows" : T.label.toLowerCase()} imported` : "Nothing was imported"}</h2>
              <p>{result.totalInvoices ? `${result.totalInvoices} invoices created · ` : ""}{result.skipped} skipped</p>
              {result.errors.length > 0 && (
                <div className="v2-inset v2im-errors">
                  {result.errors.slice(0, 50).map((e, i) => (
                    <div key={i} className="v2-inset-row v2-inset-row--static">
                      <span className="v2l-tag">Row {e.row ?? "?"}</span>
                      <span className="v2-row-sub v2-wrap">{e.errors?.join(", ") || e.message || e.reason || "Unknown error"}</span>
                    </div>))}
                </div>
              )}
              <div className="v2ci-done-actions">
                <button type="button" className="v2-btn" onClick={reset}>Import another file</button>
                {result.imported > 0 && <button type="button" className="v2-btn v2-btn--primary" onClick={() => navigate(T.after)}>View {T.label.toLowerCase()} <Glyph name="chevronRight" size={15} stroke={2} /></button>}
              </div>
            </div>
          ) : preview ? (
            <>
              <div className="v2im-filebar">
                <Duo name="note" size={28} />
                <span className="v2-row-main"><span className="v2-row-title">{file?.name}</span><span className="v2-row-sub">{preview.total} row{preview.total === 1 ? "" : "s"} · {preview.columns.length} columns</span></span>
                <button type="button" className="v2-btn v2-btn--sm" onClick={reset}>Change file</button>
              </div>
              <div className="v2c-section-title" style={{ margin: "16px 0 8px 4px" }}>Columns to import</div>
              <div className="v2i-presets">
                {preview.columns.map((c) => {
                  const known = T.columns.includes(c);
                  return (
                    <button key={c} type="button" className={`v2-chip-btn v2i-preset${cols[c] ? " is-on" : ""}${!known ? " is-unknown" : ""}`} onClick={() => setCols((p) => ({ ...p, [c]: !p[c] }))} title={known ? "" : "Not a recognised column — it will be ignored by the server"}>
                      {cols[c] && <Glyph name="check" size={12} stroke={2.6} />} {c}{T.required.includes(c) ? " *" : ""}
                    </button>);
                })}
              </div>
              {missing.length > 0 && <div className="v2-banner v2-banner--warn" style={{ marginTop: 12 }}><Glyph name="alert" size={16} /> Required column{missing.length === 1 ? "" : "s"} missing or unselected: {missing.join(", ")}</div>}
              <div className="v2c-section-title" style={{ margin: "18px 0 8px 4px" }}>Preview · first {preview.rows.length} rows</div>
              <div className="v2im-table">
                <table>
                  <thead><tr>{preview.columns.filter((c) => cols[c]).map((c) => <th key={c}>{c}</th>)}</tr></thead>
                  <tbody>{preview.rows.map((r, i) => <tr key={i}>{preview.columns.filter((c) => cols[c]).map((c) => <td key={c}>{r[c]}</td>)}</tr>)}</tbody>
                </table>
              </div>
              {err && <div className="v2-banner v2-banner--bad" style={{ marginTop: 12 }}><Glyph name="alert" size={16} /> {err}</div>}
              <div className="v2im-actions">
                <span className="v2-row-sub">{selected.length} of {preview.columns.length} columns · {preview.total} rows</span>
                <button type="button" className="v2-btn v2-btn--primary" disabled={busy || !selected.length} onClick={run}>{busy ? "Importing…" : `Import ${preview.total} rows`}</button>
              </div>
            </>
          ) : (
            <>
              <label className={`v2im-drop${drag ? " is-drag" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
                onDrop={(e) => { e.preventDefault(); setDrag(false); take(e.dataTransfer.files?.[0]); }}>
                <Duo name="note" size={48} />
                <strong>Drop your {T.label.toLowerCase()} CSV here</strong>
                <span>or <u>choose a file</u> — you'll see a preview before anything is imported</span>
                <input ref={input} type="file" accept=".csv,text/csv" hidden onChange={(e) => { take(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
              {err && <div className="v2-banner v2-banner--bad" style={{ marginTop: 12 }}><Glyph name="alert" size={16} /> {err}</div>}
            </>
          )}
        </section>

        <aside className="v2-card v2im-side">
          <div className="v2ci-sec-head"><Duo name={T.glyph} size={26} /><h2>{T.label} format</h2></div>
          <button type="button" className="v2-btn v2-btn--sm" onClick={template}><Glyph name="download" size={15} /> Download template</button>
          <div className="v2im-cols">{T.columns.map((c) => <span key={c} className={T.required.includes(c) ? "is-req" : ""}>{c}</span>)}</div>
          <p className="v2-row-sub"><b>Bold</b> columns are required.</p>
          <ul className="v2im-hints">{T.hints.map((h) => <li key={h}>{h}</li>)}</ul>
        </aside>
      </div>
    </div>
  );
}
